import { Injectable, Signal, inject, signal } from '@angular/core';
import { AnnotationStore } from './annotation-store';
import { AnnotationRow, PdfRect } from './types';

export interface PendingDraft {
  pageNumber: number;
  rect: PdfRect;
  kind: 'box' | 'text';
}

export interface PendingTextPoint {
  pageNumber: number;
  pdfX: number;
  pdfY: number;
  clientX: number;
  clientY: number;
}

@Injectable()
export class AnnotationWorkflow {
  private store = inject(AnnotationStore);

  private readonly _pendingDraft = signal<PendingDraft | null>(null);
  private readonly _pendingTextPoint = signal<PendingTextPoint | null>(null);
  private readonly _pendingTextContent = signal<string | null>(null);
  private readonly _editingNoteForId = signal<string | null>(null);
  private readonly _editingOcrForId = signal<string | null>(null);

  readonly pendingDraft: Signal<PendingDraft | null> = this._pendingDraft.asReadonly();
  readonly pendingTextPoint: Signal<PendingTextPoint | null> = this._pendingTextPoint.asReadonly();
  readonly editingNoteForId: Signal<string | null> = this._editingNoteForId.asReadonly();
  readonly editingOcrForId: Signal<string | null> = this._editingOcrForId.asReadonly();

  proposeBox(pageNumber: number, rect: PdfRect): void {
    this._pendingDraft.set({ pageNumber, rect, kind: 'box' });
  }

  proposeTextPoint(point: PendingTextPoint): void {
    this._pendingTextPoint.set(point);
  }

  provideTextContent(payload: { text: string; pdfWidth: number; pdfHeight: number }): void {
    const point = this._pendingTextPoint();
    if (!point) return;
    this._pendingDraft.set({
      pageNumber: point.pageNumber,
      rect: {
        x: point.pdfX,
        y: point.pdfY - payload.pdfHeight,
        width: payload.pdfWidth,
        height: payload.pdfHeight,
      },
      kind: 'text',
    });
    this._pendingTextContent.set(payload.text);
    this._pendingTextPoint.set(null);
  }

  cancelTextInput(): void {
    this._pendingTextPoint.set(null);
  }

  commitWithClaim(claimId: string): AnnotationRow | null {
    const draft = this._pendingDraft();
    if (!draft) return null;
    const docId = this.store.activeDocumentId();
    if (!docId) {
      this.clearDraft();
      return null;
    }
    const created = this.store.addAnnotation({
      document_id: docId,
      claim_id: claimId,
      type: draft.kind,
      text: draft.kind === 'text' ? (this._pendingTextContent() ?? '') : undefined,
      page: draft.pageNumber,
      x: draft.rect.x,
      y: draft.rect.y,
      width: draft.rect.width,
      height: draft.rect.height,
      color: this.store.colorForClaim(claimId),
      session_id: this.store.activeSessionId(),
    });
    this.clearDraft();
    if (draft.kind === 'box') {
      void this.store.runOcr(created);
    }
    return created;
  }

  cancelDraft(): void {
    this.clearDraft();
  }

  editNote(annotationId: string): void {
    this._editingNoteForId.set(annotationId);
  }

  noteInitial(): string {
    const id = this._editingNoteForId();
    if (!id) return '';
    return this.store.annotations().find((a) => a.id === id)?.note ?? '';
  }

  saveNote(value: string): void {
    const id = this._editingNoteForId();
    if (!id) return;
    this.store.setNote(id, value === '' ? undefined : value);
    this._editingNoteForId.set(null);
  }

  cancelNoteEdit(): void {
    this._editingNoteForId.set(null);
  }

  editOcr(annotationId: string): void {
    this._editingOcrForId.set(annotationId);
  }

  ocrInitial(): string {
    const id = this._editingOcrForId();
    if (!id) return '';
    return this.store.annotations().find((a) => a.id === id)?.ocr_text ?? '';
  }

  saveOcr(value: string): void {
    const id = this._editingOcrForId();
    if (!id) return;
    this.store.setOcrText(id, value === '' ? '' : value);
    this._editingOcrForId.set(null);
  }

  cancelOcrEdit(): void {
    this._editingOcrForId.set(null);
  }

  private clearDraft(): void {
    this._pendingDraft.set(null);
    this._pendingTextContent.set(null);
  }
}
